<template>
	<div v-if="variant === 'pill'" class="status-container status-indicator--pill d-flex align-center" :class="[statusClass, toneClass]">
		<v-icon class="status-icon status-icon--small mr-1" :class="[statusClass, toneClass]">{{ icon }}</v-icon>
		<span class="status-text text-no-wrap">
			<slot>{{ label }}</slot>
		</span>
	</div>
	<v-avatar v-else-if="variant === 'avatar'" size="x-small" class="flex-shrink-0 pa-3" :class="[statusClass, toneClass]">
		<v-icon v-if="icon" class="flex-shrink-0">{{ icon }}</v-icon>
	</v-avatar>
	<v-icon v-else class="status-icon" :class="[statusClass, toneClass, { 'status-icon--small': small }]">{{ icon }}</v-icon>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import { getStatusIcon, getStatusTone } from "@/helpers/statusIcon";

	const props = withDefaults(
		defineProps<{
			/** A session or project status, e.g. "InProgress". */
			status: string | null | undefined;
			/**
			 * icon: the round tinted icon on its own (list rows, the status select); attributes such as size
			 *   (the glyph) and spacing classes pass through to the v-icon.
			 * avatar: the icon in a tonal x-small v-avatar (session and project cards).
			 * pill: the icon and its label on a tinted background (entity headers, mobile rows).
			 */
			variant?: "icon" | "pill" | "avatar";
			/** Pill text; the default slot replaces it. */
			label?: string;
			/** A 16px icon instead of 24px (the pill always uses it). */
			small?: boolean;
		}>(),
		{ variant: "icon", label: undefined, small: false }
	);

	const icon = computed(() => getStatusIcon(props.status));
	// status-<name> stays on the element: page objects, specs and page layout rules select on it.
	const statusClass = computed(() => (props.status ? `status-${props.status.toLowerCase()}` : undefined));
	const toneClass = computed(() => {
		const tone = getStatusTone(props.status);
		return tone ? `status-tone--${tone}` : undefined;
	});
</script>

<style scoped>
	.status-icon {
		height: 24px;
		width: 24px;
		border-radius: 12px;
	}

	.status-icon--small {
		height: 16px;
		width: 16px;
		font-size: 0.85rem;
	}

	.status-indicator--pill {
		flex: 0 0 auto;
		white-space: nowrap;
		border-radius: 5px;
	}

	.status-tone--neutral {
		background-color: rgb(var(--v-theme-status-neutral-surface));
		color: rgb(var(--v-theme-status-neutral));
	}

	.status-tone--progress {
		background-color: rgba(var(--v-theme-status-progress-tint), 0.125);
		color: rgb(var(--v-theme-status-progress));
	}

	.status-tone--review {
		background-color: rgb(var(--v-theme-status-review-surface));
		color: rgb(var(--v-theme-brand));
	}

	.status-tone--done {
		background-color: rgba(var(--v-theme-status-done), 0.125);
		color: rgb(var(--v-theme-status-done));
	}
</style>
